import { PortableText } from '@portabletext/react'
import { ArbitraryTypedObject, PortableTextBlock } from '@portabletext/types'
import { useContext } from 'react'

import { recursiveUpdateValue } from '@lib/helpers'
import { LanguageContext } from '@lib/language'
import { getTextWithShippingDates } from '@lib/shipping'
import { SiteContext } from '@lib/site'

import AddToCartButton from './blocks/add-to-cart-button'
import ColoredText from './blocks/colored-text'
import Figure from './blocks/figure'
import HorizontalRule from './blocks/horizontal-rule'
import Link from './blocks/link'
import Quote from './blocks/quote'
import VideoButton from './blocks/video-button'
import VideoEmbed from './blocks/video-embed'

type ComplexPortableTextBlock = PortableTextBlock | ArbitraryTypedObject

interface ComplexPortableTextProps {
  content: ComplexPortableTextBlock[]
  className?: string
}

const ComplexPortableText = ({
  content,
  className,
}: ComplexPortableTextProps) => {
  const { locale } = useContext(LanguageContext)
  const { cartSettings } = useContext(SiteContext)

  const contentWithVariables = recursiveUpdateValue(
    content,
    (value, currentKey) => {
      if (
        currentKey === 'text' &&
        value &&
        typeof value === 'string' &&
        value.includes('{') &&
        value.includes('}') &&
        cartSettings
      ) {
        return getTextWithShippingDates(locale, cartSettings, value)
      }

      return value
    },
  ) as ComplexPortableTextBlock[]

  return (
    <div className={className}>
      <PortableText
        value={contentWithVariables}
        components={{
          block: {
            none: ({ children }) => <>{children}</>,
            normal: ({ children }) => <p>{children}</p>,
            preamble: ({ children }) => (
              <p className="!mb-6 text-2xl">{children}</p>
            ),
            h1: ({ children }) => <h1>{children}</h1>,
            h2: ({ children }) => <h2>{children}</h2>,
            h3: ({ children }) => <h3>{children}</h3>,
            h4: ({ children }) => <h4>{children}</h4>,
            h1mock: ({ children }) => <p className="is-h1">{children}</p>,
            h2mock: ({ children }) => <p className="is-h2">{children}</p>,
            h3mock: ({ children }) => <p className="is-h3">{children}</p>,
            h4mock: ({ children }) => <p className="is-h4">{children}</p>,
            normalserif: ({ children }) => (
              <p className="!font-serif">{children}</p>
            ),
            preambleserif: ({ children }) => (
              <p className="!mb-6 text-2xl !font-serif">{children}</p>
            ),
            h1serif: ({ children }) => (
              <h1 className="!font-serif">{children}</h1>
            ),
            h2serif: ({ children }) => (
              <h2 className="!font-serif">{children}</h2>
            ),
            h3serif: ({ children }) => (
              <h3 className="!font-serif">{children}</h3>
            ),
            h4serif: ({ children }) => (
              <h4 className="!font-serif">{children}</h4>
            ),
            h1mockserif: ({ children }) => (
              <p className="is-h1 !font-serif">{children}</p>
            ),
            h2mockserif: ({ children }) => (
              <p className="is-h2 !font-serif">{children}</p>
            ),
            h3mockserif: ({ children }) => (
              <p className="is-h3 !font-serif">{children}</p>
            ),
            h4mockserif: ({ children }) => (
              <p className="is-h4 !font-serif">{children}</p>
            ),
          },
          types: {
            addToCartButton: AddToCartButton,
            figure: Figure,
            horizontalRule: HorizontalRule,
            quote: Quote,
            videoButton: VideoButton,
            videoEmbed: VideoEmbed,
          },
          marks: {
            link: Link,
            textColor: ColoredText,
          },
        }}
      />
    </div>
  )
}

export default ComplexPortableText
